import { createBrowserRouter, Route, RouteObject, Routes, matchPath, useLocation, Navigate } from 'react-router-dom';
import { JSX, lazy, Suspense } from 'react';
import ErrorBoundary from 'components/common/Error/ErrorBoundary';
import { RootLayout, SettingLayout } from 'layouts';
import { LoginPage } from 'pages';
import useAuthContext from 'stores/auth-context';
import { LoadingFallback } from 'components/common';
import { AppRouteType } from 'types';
import CreateCheckOrderPage from 'pages/CreateCheckOrderPage';
import MaterialListPage from 'pages/MaterialListPage';
import DiscountCodeListPage from 'pages/DiscountCodeListPage';
import DetailReturnOrderPage from 'pages/DetailReturnOrderPage';
import { LayoutNoHeader } from 'layouts/LayoutNoHeader';
import PaymentOrderListPage from 'pages/PaymentOrderListPage';
import InvoicePage from 'pages/InvoicePage';
import InvoiceListPage from 'pages/Invoice/InvoiceListPage';
import envService, { HiddenFeatures } from 'services/env.service';
import Forbidden from 'pages/403';
import RequirePermission from 'pages/RequirePermission';
import { PERMISSIONS } from 'constant/permission';

// Lazy-loaded routes for code splitting
// const HomePage = lazy(() => import('pages/HomePage'));
const AccountingRevenuePage = lazy(() => import('pages/Accounting/AccountingRevenuePage'));
const CreateCustomerPage = lazy(() => import('pages/CreateCustomerPage'));
const ManagerCustomerPage = lazy(() => import('pages/ManageCustomerPage'));
const NotFound = lazy(() => import('pages/NotFound'));
const ForgotPasswordPage = lazy(() => import('pages/ForgotPasswordPage'));
const ResetPassword = lazy(() => import('pages/ResetPassword'));
const CategoryPage = lazy(() => import('pages/CategoryPage'));
const ProductListPage = lazy(() => import('pages/ProductListPage'));
const SettingPage = lazy(() => import('pages/SettingPage'));
const ItemPage = lazy(() => import('pages/ItemPage'));
const UnitListPage = lazy(() => import('pages/UnitListPage'));
const BrandListPage = lazy(() => import('pages/BrandListPage'));
const AttributeListPage = lazy(() => import('pages/AttributeListPage'));
const CategoryListPage = lazy(() => import('pages/CategoryListPage'));
const CreateProductPage = lazy(() => import('pages/CreateProductPage'));
const DetailProductPage = lazy(() => import('pages/DetailProductPage'));
const PurchaseOrderListPage = lazy(() => import('pages/PurchaseOrderListPage'));
const ImportOrderPage = lazy(() => import('pages/ImportOrderPage'));
const SupplierOrderListPage = lazy(() => import('pages/SupplierOrderListPage'));
const CreateOrderSupplier = lazy(() => import('pages/CreatOrderSupplier'));
const SupplierListPage = lazy(() => import('pages/SupplierListPage'));
const CreateSupplier = lazy(() => import('pages/SupplierPage'));
const CheckOrderListPage = lazy(() => import('pages/CheckOrderListPage'));
const ManagementPage = lazy(() => import('pages/ManagementPage'));
const PaymentWaitPage = lazy(() => import('pages/PaymentWaitPage'));
const EditOrderSupplier = lazy(() => import('pages/EditOrderSupplier'));
const CustomerListPage = lazy(() => import('pages/CustomerListPage'));
const CreateSaleOrderPage = lazy(() => import('pages/CreateSaleOrderPage'));
const OutOrderListPage = lazy(() => import('pages/OutOrderListPage'));
const CreateStockOutOrder = lazy(() => import('pages/CreateStockOutOrder'));
const StockOutOrderDetail = lazy(() => import('pages/StockOutOrderDetail'));
const CreateProductSupplierPage = lazy(() => import('pages/CreateProductSupplierPage'));
const DebtListPage = lazy(() => import('pages/DebtListPage'));
const CompanyProfilePage = lazy(() => import('pages/CompanyProfilePage'));
const SaleOrderListPage = lazy(() => import('pages/SaleOrderListPage'));
const DetailSaleOrderPage = lazy(() => import('pages/DetailSaleOrderPage'));
const DetailCheckOrderPage = lazy(() => import('pages/DetailCheckOrderPage'));
const CreatePurchaseOrderPage = lazy(() => import('pages/CreatePurchaseOrderPage'));
const DashboardPage = lazy(() => import('pages/DashboardPage'));
const StaffListPage = lazy(() => import('pages/StaffListPage'));
const MemberRankPage = lazy(() => import('pages/MemberRankPage'));
const CreateReturnOrderPage = lazy(() => import('pages/CreateReturnOrderPage'));
const CreateInvoice = lazy(() => import('pages/Invoice/CreateInvoicePage'));
const ViewDetailInvoicePage = lazy(() => import('pages/Invoice/ViewDetailInvoicePage'));
const SystemPermissionPage = lazy(() => import('pages/Permission/SystemPermissionPage'));
export let routes: (AppRouteType | RouteObject)[] = [
  {
    path: '/',
    element: <CategoryPage />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    path: '/dashboard',
    element: (
      <RequirePermission permission={PERMISSIONS.DASHBOARD}>
        <DashboardPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    // noLayout: true,
  },
  {
    path: '/settings/units',
    element: (
      <RequirePermission permission={PERMISSIONS.UNIT}>
        <UnitListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
    noWrapper: true,
    setting: true,
  },
  {
    path: '/settings/categories',
    element: (
      <RequirePermission permission={PERMISSIONS.CATEGORY}>
        <CategoryListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
    noWrapper: true,
    setting: true,
  },

  {
    path: '/settings/brands',
    element: (
      <RequirePermission permission={PERMISSIONS.BRAND}>
        <BrandListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
    noWrapper: true,
    setting: true,
  },
  {
    path: '/settings/attributes',
    element: (
      <RequirePermission permission={PERMISSIONS.BRAND}>
        <AttributeListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
    noWrapper: true,
    setting: true,
  },
  {
    path: '/settings/company-profile',
    element: (
      <RequirePermission permission={PERMISSIONS.COMPANY_PROFILE}>
        <CompanyProfilePage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/settings/staff',
    element: (
      <RequirePermission permission={PERMISSIONS.STAFF}>
        <StaffListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    noLayout: true,
    isPublic: false,
    setting: true,
    noWrapper: true,
  },
  {
    path: '/settings/member-ranks',
    element: (
      <RequirePermission permission={PERMISSIONS.MEMBER_RANK}>
        <MemberRankPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    noLayout: true,
    isPublic: false,
    noWrapper: true,
    setting: true,
  },
  {
    path: '/settings/materials',
    element: (
      <RequirePermission permission={PERMISSIONS.MATERIAL}>
        <MaterialListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    noLayout: true,
    isPublic: false,
    noWrapper: true,
    setting: true,
  },
  {
    path: '/settings/discount-codes',
    element: (
      <RequirePermission permission={PERMISSIONS.DISCOUNT_CODE}>
        <DiscountCodeListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    noLayout: true,
    isPublic: false,
    noWrapper: true,
    setting: true,
  },
  {
    path: '/settings/system-permissions',
    element: (
      <RequirePermission permission={PERMISSIONS.PERMISSION}>
        <SystemPermissionPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    noLayout: true,
    isPublic: false,
    noWrapper: true,
    setting: true,
  },
  {
    path: '/settings',
    element: <SettingPage />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/items',
    element: (
      <RequirePermission permission={PERMISSIONS.PRODUCT}>
        <ItemPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/items/products',
    element: (
      <RequirePermission permission={PERMISSIONS.PRODUCT}>
        <ProductListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/management/inventory/purchase-orders',
    element: (
      <RequirePermission permission={PERMISSIONS.PURCHASE_ORDER}>
        <PurchaseOrderListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/management/inventory/purchase-orders/importing/:id',
    element: (
      <RequirePermission permission={PERMISSIONS.PURCHASE_ORDER}>
        <ImportOrderPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    path: '/management/inventory/purchase-orders/create',
    element: (
      <RequirePermission permission={PERMISSIONS.PURCHASE_ORDER_CREATE}>
        <CreatePurchaseOrderPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    path: '/management',
    element: <ManagementPage />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/management/inventory/check-orders',
    element: (
      <RequirePermission permission={PERMISSIONS.INVENTORY_CHECK_ODER}>
        <CheckOrderListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/management/inventory/check-orders/create',
    element: (
      <RequirePermission permission={PERMISSIONS.INVENTORY_CHECK_ODER_CREATE}>
        <CreateCheckOrderPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    path: '/management/inventory/check-orders/detail/:id',
    element: (
      <RequirePermission permission={PERMISSIONS.INVENTORY_CHECK_ODER_READ}>
        <DetailCheckOrderPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },

  {
    path: '/management/inventory/out-orders',
    element: (
      <RequirePermission permission={PERMISSIONS.STOCK_OUT_ORDER}>
        <OutOrderListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/management/inventory/out-orders/create',
    element: (
      <RequirePermission permission={PERMISSIONS.INVENTORY_CHECK_ODER_CREATE}>
        <CreateStockOutOrder />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    path: '/management/inventory/out-orders/:id',
    element: <StockOutOrderDetail />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    path: '/management/inventory/return-orders/create',
    element: (
      <RequirePermission permission={PERMISSIONS.RETURN_PURCHASE_CREATE}>
        <CreateReturnOrderPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    path: '/management/inventory/return-orders',
    element: (
      <RequirePermission permission={PERMISSIONS.RETURN_PURCHASE}>
        <PaymentOrderListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/management/inventory/return-orders/detail/:id',
    element: (
      <RequirePermission permission={PERMISSIONS.RETURN_PURCHASE_READ}>
        <DetailReturnOrderPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    path: '/management/debts',
    element: (
      <RequirePermission permission={PERMISSIONS.DEBT}>
        <DebtListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: 'management/customers',
    element: (
      <RequirePermission permission={PERMISSIONS.CUSTOMER}>
        <CustomerListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: 'management/customers/create',
    element: (
      <RequirePermission permission={PERMISSIONS.CUSTOMER_CREATE}>
        <CreateCustomerPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: 'management/customers/tabs/:id',
    element: (
      <RequirePermission permission={PERMISSIONS.CUSTOMER}>
        <ManagerCustomerPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },

  {
    path: 'management/customers/create',
    element: (
      <RequirePermission permission={PERMISSIONS.CUSTOMER_CREATE}>
        <CreateCustomerPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: 'management/customers/create',
    element: (
      <RequirePermission permission={PERMISSIONS.CUSTOMER_CREATE}>
        <CreateCustomerPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    name: 'Login',
    path: '/login',
    element: <LoginPage />,
    auth: false,
    isPublic: true,
  },
  {
    name: 'ForgotPassword',
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
    auth: false,
    isPublic: true,
  },
  {
    name: 'ResetPassword',
    path: '/reset-password',
    element: <ResetPassword />,
    auth: false,
    isPublic: true,
  },
  {
    name: 'CreateProduct',
    path: '/items/products/create-product',
    element: (
      <RequirePermission permission={PERMISSIONS.PRODUCT_CREATE}>
        <CreateProductPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    name: 'DetailProduct',
    path: '/items/products/detail-product/:productId/:versionId',
    element: (
      <RequirePermission permission={PERMISSIONS.PRODUCT_READ}>
        <DetailProductPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    name: 'DetailSupplierOrder',
    path: '/management/inventory/supplier-orders',
    element: (
      <RequirePermission permission={PERMISSIONS.SUPPLIER}>
        <SupplierOrderListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },

  {
    name: 'SaleOrderList',
    path: '/management/sale-orders',
    element: (
      <RequirePermission permission={PERMISSIONS.SALE_ORDER}>
        <SaleOrderListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    name: 'DetailSaleOrder',
    path: '/management/sale-orders/detail',
    element: (
      <RequirePermission permission={PERMISSIONS.SALE_ORDER_READ}>
        <DetailSaleOrderPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    name: 'SupplierList',
    path: '/management/suppliers',
    element: (
      <RequirePermission permission={PERMISSIONS.SUPPLIER}>
        <SupplierListPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    name: 'CreateSupplier',
    path: '/management/suppliers/create',
    element: (
      <RequirePermission permission={PERMISSIONS.SUPPLIER_CREATE}>
        <CreateSupplier />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    name: 'CreateSupplierOrder',
    path: '/management/inventory/supplier-orders/create',
    element: (
      <RequirePermission permission={PERMISSIONS.ORDER_SUPPLIER_CREATE}>
        <CreateOrderSupplier />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    name: 'EditSupplierOrder',
    path: '/management/inventory/supplier-orders/edit/:id',
    element: (
      <RequirePermission permission={PERMISSIONS.ORDER_SUPPLIER_UPDATE}>
        <EditOrderSupplier />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    name: 'DetailSupplier',
    path: '/management/suppliers/detail-supplier/:id',
    element: (
      <RequirePermission permission={PERMISSIONS.ORDER_SUPPLIER_READ}>
        <CreateSupplier />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    noLayout: true,
    isPublic: false,
  },
  {
    name: 'ViewSupplierOrder',
    path: '/management/inventory/supplier-orders/view/:id',
    element: (
      <RequirePermission permission={PERMISSIONS.ORDER_SUPPLIER}>
        <PaymentWaitPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    name: 'PaymentWaitSupplierOrder',
    path: '/management/inventory/supplier-orders/payment-wait/:id',
    element: (
      <RequirePermission permission={PERMISSIONS.ORDER_SUPPLIER_READ}>
        <PaymentWaitPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/create-sale-order',
    element: (
      <RequirePermission permission={PERMISSIONS.SALE_ORDER}>
        <CreateSaleOrderPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
    noLayout: true,
    noWrapper: true,
  },
  {
    path: '/management/suppliers/create-product/:id',
    element: (
      <RequirePermission permission={PERMISSIONS.ORDER_SUPPLIER_CREATE}>
        <CreateProductSupplierPage />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: '*',
    element: <NotFound />,
    auth: true,
    isPublic: false,
    noLayout: true,
  },
  {
    path: '/accounting',
    element: <Navigate to='/accounting/s2a' replace />,
    auth: true,
    isPublic: false,
  },
  {
    path: '/accounting/s2a',
    element: (
      <RequirePermission permission={PERMISSIONS.TRANSACTION}>
        <AccountingRevenuePage type='S2a' />
      </RequirePermission>
    ),
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  // {
  //   path: '/accounting/s2b',
  //   element: (
  //       <AccountingRevenuePage type='S2b'/>
  //   ),
  //   errorElement: <ErrorBoundary />,
  //   auth: true,
  //   isPublic: false,
  // },
  // {
  //   path: '/accounting/s2d',
  //   element: <AccountingProductPage />,
  //   errorElement: <ErrorBoundary />,
  //   auth: true,
  //   isPublic: false,
  // },
];

if (!envService.isFeatureHidden(HiddenFeatures.EInvoice)) {
  routes = [
    ...routes,
    {
      path: '/invoices',
      element: (
        <RequirePermission permission={PERMISSIONS.E_INVOICE}>
          <InvoicePage />
        </RequirePermission>
      ),
      errorElement: <ErrorBoundary />,
      auth: true,
      isPublic: false,
    },
    {
      path: '/invoices/electronic-invoices',
      element: (
        <RequirePermission permission={PERMISSIONS.E_INVOICE}>
          <InvoiceListPage />
        </RequirePermission>
      ),
      errorElement: <ErrorBoundary />,
      auth: true,
      isPublic: false,
    },
    {
      path: '/invoices/electronic-invoices/create-invoice',
      element: (
        <RequirePermission permission={PERMISSIONS.E_INVOICE_CREATE}>
          <CreateInvoice />
        </RequirePermission>
      ),
      errorElement: <ErrorBoundary />,
      auth: true,
      isPublic: false,
      // noWrapper: true,
      noLayout: true,
    },
    {
      path: '/invoices/electronic-invoices/view/:id',
      element: (
        <RequirePermission permission={PERMISSIONS.E_INVOICE_READ}>
          <ViewDetailInvoicePage />
        </RequirePermission>
      ),
      errorElement: <ErrorBoundary />,
      auth: true,
      isPublic: false,
      // noWrapper: true,
      noLayout: true,
    },
    {
      path: '/403',
      element: <Forbidden />,
      errorElement: <ErrorBoundary />,
      auth: true,
      isPublic: false,
      noLayout: true,
    },
  ];
}

// Create the router instance
const router = createBrowserRouter(routes);

export const BasePage = router;

function waitFor(Component: undefined | JSX.Element) {
  if (!Component) {
    // Return a fallback component if the component is undefined
    return <NotFound />;
  }
  return <Suspense fallback={<LoadingFallback />}>{Component}</Suspense>;
}
export default function RoutesAppRoutes() {
  const { accessToken } = useAuthContext();
  const location = useLocation();

  const publicRoutes = (routes as AppRouteType[])
    .filter((route) => !route.auth || route.isPublic)
    .map((route) => <Route key={route.path} path={route.path} element={waitFor(route.element)} />);

  // public routes
  if (!accessToken) return <Routes>{publicRoutes}</Routes>;
  // 404 routes
  const isNotFoundPage = !routes.some(
    (route) => route.path !== '*' && route.path && matchPath(route.path, location.pathname),
  );
  if (isNotFoundPage) {
    return (
      <Routes>
        <Route path='*' element={waitFor(<NotFound />)} />
      </Routes>
    );
  }
  const authenticatedRoutes = (routes as AppRouteType[])
    .filter((route) => ((route.auth && !route.permissions) || route.isPublic) && route.path !== '*')
    .map((route) => {
      const routeElement = route.setting ? (
        <SettingLayout>{waitFor(route.element)}</SettingLayout>
      ) : route.noLayout && !route.noWrapper ? (
        <LayoutNoHeader>{waitFor(route.element)}</LayoutNoHeader>
      ) : route.noWrapper ? (
        <>{waitFor(route.element)}</>
      ) : (
        <RootLayout>{waitFor(route.element)}</RootLayout>
      );
      return <Route key={route.path} path={route.path} element={routeElement} />;
    });

  return <Routes>{authenticatedRoutes}</Routes>;
}
